/* ==========================================================================
   Natural Nectar — free-delivery progress bar

   Keeps every <nn-shipping-progress> on the page in sync with the real cart.

   Why it re-fetches /cart.js rather than reading the pub/sub payload:
   Dawn publishes `cart-update` from two places with different shapes. cart.js
   sends the full parsed cart (has total_price); product-form.js sends the
   /cart/add.js response, which describes the line just added and has no
   reliable cart total. Re-reading /cart.js is one small request and gives the
   same answer in both cases, so the bar can never drift.
   ========================================================================== */

class NNShippingProgress extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.textEl = this.querySelector('[data-nn-ship-text]');
    this.fillEl = this.querySelector('[data-nn-ship-fill]');

    // Dawn's pub/sub is loaded globally; guard anyway so a missing constants.js
    // degrades to a static (still correct on load) bar rather than throwing.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
    }

    // The notification and drawer variants are cloned into the DOM after a
    // cart update has already fired, so read the truth once on connect too.
    if (this.dataset.nnShipDeferred === 'true') this.refresh();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  async refresh() {
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.render(cart.total_price);
    } catch (e) {
      // Network hiccup: leave the last known good state on screen rather than
      // showing a wrong number.
    }
  }

  render(totalPrice) {
    const remaining = Math.max(this.threshold - totalPrice, 0);
    const percent = this.threshold > 0 ? Math.min((totalPrice / this.threshold) * 100, 100) : 0;
    const unlocked = remaining <= 0;

    this.classList.toggle('is-unlocked', unlocked);
    if (this.fillEl) this.fillEl.style.width = `${percent}%`;
    if (!this.textEl) return;

    const icon = this.textEl.querySelector('.nn-ship__icon');
    const iconHTML = icon ? icon.outerHTML : '';

    this.textEl.innerHTML = unlocked
      ? `${iconHTML}Free delivery unlocked on this order`
      : `${iconHTML}Add <strong>${this.money(remaining)}</strong> more for free delivery`;
  }

  /* Delegates to the shared formatter so this label is spelled exactly like
     every server-rendered price on the page. See assets/nn-money.js. */
  money(cents) {
    if (typeof window.nnFormatMoney === 'function') {
      return window.nnFormatMoney(cents, this.moneyFormat);
    }
    // nn-money.js failed to load: a bare number still beats a broken string.
    return (cents / 100).toFixed(2);
  }
}

if (!customElements.get('nn-shipping-progress')) {
  customElements.define('nn-shipping-progress', NNShippingProgress);
}
